import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  ChevronLeft,
  ChevronRight,
  Info,
  Plus,
  Settings2,
  X,
} from 'lucide-react'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'
import { Bytes } from '@/components/Bytes'

import { DataTable } from '@/components/DataTable'
import { InfoTip } from '@/components/InfoTip'
import { PageHeader } from '@/components/PageHeader'
import { PendingButton } from '@/components/PendingButton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  addMonths,
  bpToPercent,
  computeMonthlyProfitShare,
  computeProfitShare,
  currentMonthPeriod,
  type EditScope,
  editConflicts,
  emptySnapshot,
  type IncomePeriod,
  type MonthKey,
  type MonthProfitShare,
  monthKey,
  monthKeyLabel,
  monthKeyOfDate,
  monthKeyShortLabel,
  monthsInPeriod,
  type ProfitShareBreakdown,
  type ProfitShareEditChange,
  type ProfitShareSnapshot,
  percentToBp,
  periodLabel,
  resolveSnapshot,
  shiftPeriod,
} from '@/lib/admin/income'
import {
  addPaymentCommission,
  archiveProfitShareEmployee,
  getIncomeSummary,
  getOrgIncomePayments,
  getProfitShareConfig,
  type IncomeOrgRow,
  type IncomeSummary,
  movePaymentMonth,
  type ProfitShareConfig,
  type ProfitShareEmployeeRow,
  removePaymentCommission,
  saveProfitShareEmployee,
  updateProfitShareSettings,
} from '@/lib/admin/income-server'
import { formatMicroUsd } from '@/lib/billing/billing'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/admin/income')({
  component: AdminIncomePage,
})

const CONFIG_KEY = ['admin-profit-share'] as const

function AdminIncomePage() {
  const [period, setPeriod] = useState<IncomePeriod>(() => currentMonthPeriod())
  const summary = useQuery({
    queryKey: ['admin-income', period],
    queryFn: () => getIncomeSummary({ data: { period } }),
  })
  const config = useQuery({
    queryKey: CONFIG_KEY,
    queryFn: () => getProfitShareConfig(),
  })
  const queryClient = useQueryClient()
  const [commissionFor, setCommissionFor] = useState<IncomeOrgRow | null>(null)
  const [paymentsFor, setPaymentsFor] = useState<IncomeOrgRow | null>(null)
  const canEdit = config.data?.canEdit ?? false
  const removeCommission = useMutation({
    mutationFn: (args: { organizationId: string; employeeId: string }) =>
      removePaymentCommission({ data: { ...args, period } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-income'] })
      toast.success('Commission removed')
    },
    onError: (e) =>
      toast.error("Couldn't remove", { description: (e as Error).message }),
  })
  const columns = orgColumns(
    canEdit,
    setPaymentsFor,
    setCommissionFor,
    (organizationId, employeeId) =>
      removeCommission.mutate({ organizationId, employeeId }),
  )

  const stats = [
    {
      label: 'Received',
      value:
        summary.data === undefined
          ? undefined
          : formatMicroUsd(summary.data.totalMicros),
    },
    {
      label: 'Data funded',
      value:
        summary.data === undefined ? undefined : (
          <Bytes value={summary.data.totalBytes} unit="gb" />
        ),
    },
    { label: 'Paying organizations', value: summary.data?.rows.length },
    { label: 'Payments', value: summary.data?.totalPayments },
  ]

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Income"
        description="Money received per organization, and how the profit-share pool divides."
        actions={<PeriodPicker period={period} onChange={setPeriod} />}
      />

      {period.kind === 'month' ? (
        <Alert>
          <Info />
          <AlertTitle>Calendar months, not billing cycles</AlertTitle>
          <AlertDescription>
            Payments are bucketed by the day the money was recorded. Invoiced
            and crypto terms run 30 days and Stripe subscriptions renew on their
            own anchor date, so a customer can land twice in one month and not
            at all in the next. Click an organization's payment count to move a
            payment into the month before or after.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label}>
            <CardHeader>
              <CardDescription>{s.label}</CardDescription>
              <CardTitle className="text-2xl tabular-nums">
                {s.value === undefined ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  s.value
                )}
              </CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By organization</CardTitle>
          <CardDescription>
            {periodLabel(period)}. Refunds and voided invoices are netted out.
            Commissions come off the top before profit sharing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={summary.data?.rows}
            isPending={summary.isPending}
            emptyMessage="No payments in this period."
            pageSize={50}
            initialSorting={[{ id: 'micros', desc: true }]}
          />
        </CardContent>
      </Card>

      <ProfitShareCard
        period={period}
        summary={summary.data}
        config={config.data}
      />

      <Dialog
        open={commissionFor !== null}
        onOpenChange={(open) => {
          if (!open) setCommissionFor(null)
        }}
      >
        {commissionFor && config.data ? (
          <CommissionDialog
            key={commissionFor.orgId}
            org={commissionFor}
            period={period}
            employees={config.data.employees.filter((e) => !e.archivedAt)}
            onClose={() => setCommissionFor(null)}
          />
        ) : null}
      </Dialog>

      <Dialog
        open={paymentsFor !== null}
        onOpenChange={(open) => {
          if (!open) setPaymentsFor(null)
        }}
      >
        {paymentsFor ? (
          <PaymentsDialog
            key={paymentsFor.orgId}
            org={paymentsFor}
            period={period}
            canEdit={canEdit}
          />
        ) : null}
      </Dialog>
    </div>
  )
}

function PeriodPicker({
  period,
  onChange,
}: {
  period: IncomePeriod
  onChange: (p: IncomePeriod) => void
}) {
  const now = currentMonthPeriod()
  const year = period.kind === 'all' ? now.year : period.year
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToggleGroup
        type="single"
        size="sm"
        variant="outline"
        spacing={0}
        value={period.kind}
        onValueChange={(kind) => {
          if (kind === 'month') onChange({ kind, year, month: now.month })
          else if (kind === 'year') onChange({ kind, year })
          else if (kind === 'all') onChange({ kind })
        }}
      >
        <ToggleGroupItem value="month">Month</ToggleGroupItem>
        <ToggleGroupItem value="year">Year</ToggleGroupItem>
        <ToggleGroupItem value="all">All time</ToggleGroupItem>
      </ToggleGroup>
      {period.kind !== 'all' ? (
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Previous period"
            onClick={() => onChange(shiftPeriod(period, -1))}
          >
            <ChevronLeft />
          </Button>
          <span className="min-w-36 text-center text-sm font-medium tabular-nums">
            {periodLabel(period)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Next period"
            onClick={() => onChange(shiftPeriod(period, 1))}
          >
            <ChevronRight />
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function orgColumns(
  canEdit: boolean,
  onShowPayments: (row: IncomeOrgRow) => void,
  onAddCommission: (row: IncomeOrgRow) => void,
  onRemoveCommission: (organizationId: string, employeeId: string) => void,
): ColumnDef<IncomeOrgRow>[] {
  return [
    {
      accessorKey: 'orgName',
      header: 'Organization',
      enableSorting: true,
      cell: ({ row }) => (
        <Link
          to="/admin/organizations/$orgId"
          params={{ orgId: row.original.orgId }}
          className="font-medium hover:underline"
        >
          {row.original.orgName}
        </Link>
      ),
    },
    {
      id: 'rails',
      header: 'Paid via',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.rails.map((r) => (
            <Badge key={r} variant="outline">
              {r}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'payments',
      header: 'Payments',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 tabular-nums"
            onClick={() => onShowPayments(row.original)}
          >
            {row.original.payments}
          </Button>
          {row.original.moved > 0 ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary">{row.original.moved} moved</Badge>
              </TooltipTrigger>
              <TooltipContent>
                Counted in a different month than recorded
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: 'bytes',
      header: 'Data',
      enableSorting: true,
      cell: ({ row }) => <Bytes value={row.original.bytes} unit="gb" />,
    },
    {
      accessorKey: 'micros',
      header: 'Total',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatMicroUsd(row.original.micros)}
        </span>
      ),
    },
    {
      id: 'commissions',
      header: 'Commission',
      cell: ({ row }) => (
        <div className="flex flex-col items-start gap-1">
          {row.original.commissions.map((c) => (
            <div
              key={`${c.employeeId}:${c.rateBp}`}
              className="flex items-center gap-1 text-sm"
            >
              <span>
                {c.employeeName}{' '}
                <span className="text-muted-foreground tabular-nums">
                  {bpToPercent(c.rateBp)}%
                </span>{' '}
                <span className="font-medium tabular-nums">
                  {formatMicroUsd(c.micros)}
                </span>
              </span>
              {canEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove ${c.employeeName}'s commission`}
                  onClick={() =>
                    onRemoveCommission(row.original.orgId, c.employeeId)
                  }
                >
                  <X />
                </Button>
              ) : null}
            </div>
          ))}
          {canEdit ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-2 h-7 text-muted-foreground"
              onClick={() => onAddCommission(row.original)}
            >
              <Plus data-icon="inline-start" />
              {row.original.commissions.length
                ? 'Add another'
                : 'Add commission'}
            </Button>
          ) : row.original.commissions.length === 0 ? (
            <span className="text-sm text-muted-foreground">None</span>
          ) : null}
        </div>
      ),
    },
  ]
}

function ProfitShareCard({
  period,
  summary,
  config,
}: {
  period: IncomePeriod
  summary: IncomeSummary | undefined
  config: ProfitShareConfig | undefined
}) {
  const [editing, setEditing] = useState<'settings' | 'new' | string | null>(
    null,
  )
  const ready = config !== undefined && summary !== undefined
  const viewed =
    period.kind === 'month' ? monthKey(period.year, period.month) : null
  const canEdit = config?.canEdit ?? false
  const editable =
    canEdit &&
    viewed !== null &&
    config !== undefined &&
    viewed <= config.currentMonth
  const names = Object.fromEntries(
    (config?.employees ?? []).map((e) => [e.id, e.name]),
  )

  const snapshot =
    ready && viewed
      ? (resolveSnapshot(config.snapshots, viewed) ?? emptySnapshot(viewed))
      : null
  const share =
    ready && snapshot
      ? computeProfitShare({
          revenueMicros: summary.totalMicros,
          commissionMicros: summary.commissionMicros,
          poolShareBp: snapshot.poolShareBp,
          baseShareBp: snapshot.baseShareBp,
          employees: snapshot.employees.map((e) => ({
            ...e,
            name: names[e.id] ?? '',
            commissionMicros: summary.commissionsByEmployee[e.id] ?? 0,
          })),
        })
      : null
  const rows =
    ready && share
      ? employeeRows(config.employees, share, summary.commissionsByEmployee)
      : undefined
  const monthly =
    ready && !viewed
      ? computeMonthlyProfitShare({
          months: monthsInPeriod(
            period,
            summary.months[0]?.month ?? config.snapshots[0]?.month ?? null,
            config.currentMonth,
          ),
          snapshots: config.snapshots,
          income: summary.months,
          names,
        })
      : null

  const poolStats =
    share && snapshot
      ? [
          {
            label: `Pool (${bpToPercent(snapshot.poolShareBp)}% of net income)`,
            value: formatMicroUsd(share.poolMicros),
          },
          {
            label: `Base (${bpToPercent(snapshot.baseShareBp)}% of pool, split equally)`,
            value: formatMicroUsd(share.basePoolMicros),
          },
          {
            label: `Discretionary (${bpToPercent(10_000 - snapshot.baseShareBp)}% of pool)`,
            value: formatMicroUsd(share.discretionaryPoolMicros),
          },
          {
            label: `Unassigned (${bpToPercent(10_000 - share.allocatedBp)}% of discretionary)`,
            value: formatMicroUsd(share.unallocatedMicros),
            tip: UNASSIGNED_TIP,
          },
        ]
      : []

  const columns = employeeColumns(editable, (id) => setEditing(id))
  const editingRow =
    editing && editing !== 'settings' && editing !== 'new'
      ? (rows?.find((e) => e.id === editing) ?? null)
      : null

  const notes: string[] = []
  if (ready) {
    const label = period.kind === 'all' ? 'all-time' : periodLabel(period)
    notes.push(
      summary.commissionMicros > 0
        ? `Computed on ${label} income of ${formatMicroUsd(summary.totalMicros)} less ${formatMicroUsd(summary.commissionMicros)} in commissions.`
        : `Computed on ${label} income of ${formatMicroUsd(summary.totalMicros)}.`,
    )
    if (!viewed) notes.push('Each month uses its own settings.')
    if (viewed && snapshot && config.snapshots.length > 0) {
      const from = resolveSnapshot(config.snapshots, viewed)
      if (from && from.month !== viewed) {
        notes.push(`Settings unchanged since ${monthKeyLabel(from.month)}.`)
      }
    }
    if (canEdit && viewed && viewed > config.currentMonth) {
      notes.push("Future months can't be edited.")
    }
    if (!canEdit)
      notes.push('Only the finance owner can change these settings.')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profit sharing</CardTitle>
        <CardDescription>
          {ready ? notes.join(' ') : 'Loading…'}
        </CardDescription>
        {editable ? (
          <CardAction className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing('settings')}
            >
              <Settings2 data-icon="inline-start" />
              Settings
            </Button>
            <Button type="button" size="sm" onClick={() => setEditing('new')}>
              <Plus data-icon="inline-start" />
              Add employee
            </Button>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {viewed ? (
          <>
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {(share ? poolStats : Array.from({ length: 4 }, () => null)).map(
                (s, i) => (
                  <div key={s?.label ?? i} className="flex flex-col gap-1">
                    <dt className="text-sm text-muted-foreground">
                      {s ? s.label : <Skeleton className="h-4 w-32" />}
                      {s?.tip ? (
                        <InfoTip
                          label="About unassigned"
                          className="ml-1 inline-flex align-middle"
                        >
                          {s.tip}
                        </InfoTip>
                      ) : null}
                    </dt>
                    <dd className="text-xl font-semibold tabular-nums">
                      {s ? s.value : <Skeleton className="h-7 w-20" />}
                    </dd>
                  </div>
                ),
              )}
            </dl>
            <DataTable
              columns={columns}
              data={rows}
              isPending={!ready}
              emptyMessage={
                editable
                  ? 'No employees yet. Add one to start dividing the pool.'
                  : 'No employees yet.'
              }
            />
          </>
        ) : monthly && config ? (
          <ProfitShareMonthlyTable
            months={monthly}
            employees={config.employees}
            showYears={period.kind === 'all'}
          />
        ) : (
          <Skeleton className="h-64 w-full" />
        )}
      </CardContent>

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
      >
        {editing === 'settings' && config && snapshot && viewed ? (
          <SettingsDialog
            key="settings"
            config={config}
            snapshot={snapshot}
            viewed={viewed}
            onClose={() => setEditing(null)}
          />
        ) : editing !== null && editing !== 'settings' && config && viewed ? (
          <EmployeeDialog
            key={editing}
            row={editingRow}
            config={config}
            viewed={viewed}
            onClose={() => setEditing(null)}
          />
        ) : null}
      </Dialog>
    </Card>
  )
}

const UNASSIGNED_TIP =
  'Discretionary share no employee holds, plus rounding. Nobody is paid this.'

interface EmployeeShareRow {
  id: string
  name: string
  archived: boolean
  inPool: boolean
  discretionaryBp: number | null
  commissionMicros: number
  baseMicros: number | null
  discretionaryMicros: number | null
  totalMicros: number
}

/** Everyone in the month's pool, plus active employees outside it and
 *  archived ones who still earned commission that month. */
function employeeRows(
  employees: ProfitShareEmployeeRow[],
  share: ProfitShareBreakdown,
  commissionsByEmployee: Record<string, number>,
): EmployeeShareRow[] {
  const shares = new Map(share.employees.map((e) => [e.id, e]))
  return employees.flatMap((e): EmployeeShareRow[] => {
    const s = shares.get(e.id)
    const archived = e.archivedAt !== null
    if (s) {
      return [
        {
          id: e.id,
          name: e.name,
          archived,
          inPool: true,
          discretionaryBp: s.discretionaryBp,
          commissionMicros: s.commissionMicros,
          baseMicros: s.baseMicros,
          discretionaryMicros: s.discretionaryMicros,
          totalMicros: s.totalMicros,
        },
      ]
    }
    const commission = commissionsByEmployee[e.id] ?? 0
    if (archived && commission === 0) return []
    return [
      {
        id: e.id,
        name: e.name,
        archived,
        inPool: false,
        discretionaryBp: null,
        commissionMicros: commission,
        baseMicros: null,
        discretionaryMicros: null,
        totalMicros: commission,
      },
    ]
  })
}

function OptionalMicros({ value }: { value: number | null }) {
  return value === null ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="tabular-nums">{formatMicroUsd(value)}</span>
  )
}

function employeeColumns(
  canEdit: boolean,
  onEdit: (id: string) => void,
): ColumnDef<EmployeeShareRow>[] {
  const cols: ColumnDef<EmployeeShareRow>[] = [
    {
      accessorKey: 'name',
      header: 'Employee',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'font-medium',
              !row.original.inPool && 'text-muted-foreground',
            )}
          >
            {row.original.name}
          </span>
          {row.original.archived ? (
            <Badge variant="secondary">Archived</Badge>
          ) : null}
          {!row.original.inPool ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline">Not in pool</Badge>
              </TooltipTrigger>
              <TooltipContent>
                No base or discretionary share this month.
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: 'commissionMicros',
      header: 'Commission',
      cell: ({ row }) => (
        <span className="tabular-nums">
          {formatMicroUsd(row.original.commissionMicros)}
        </span>
      ),
    },
    {
      accessorKey: 'baseMicros',
      header: 'Base',
      cell: ({ row }) => <OptionalMicros value={row.original.baseMicros} />,
    },
    {
      accessorKey: 'discretionaryBp',
      header: 'Discretionary %',
      cell: ({ row }) =>
        row.original.discretionaryBp === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="tabular-nums">
            {bpToPercent(row.original.discretionaryBp)}%
          </span>
        ),
    },
    {
      accessorKey: 'discretionaryMicros',
      header: 'Discretionary',
      cell: ({ row }) => (
        <OptionalMicros value={row.original.discretionaryMicros} />
      ),
    },
    {
      accessorKey: 'totalMicros',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatMicroUsd(row.original.totalMicros)}
        </span>
      ),
    },
  ]
  if (canEdit) {
    cols.push({
      id: 'actions',
      cell: ({ row }) =>
        row.original.archived ? null : (
          <div className="text-right">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onEdit(row.original.id)}
            >
              Edit
            </Button>
          </div>
        ),
    })
  }
  return cols
}

const STICKY_LEFT = 'sticky left-0 z-10 border-r bg-card'
const STICKY_RIGHT = 'sticky right-0 z-10 border-l bg-card text-right'

/** Discretionary or settings % under a month's $, in full color with an arrow
 *  where it differs from the month before. */
function PercentNote({ bp, prev }: { bp: number; prev: number | null }) {
  const changed = prev !== null && prev !== bp
  return (
    <span
      className={cn(
        'text-xs tabular-nums',
        changed ? 'font-medium text-foreground' : 'text-muted-foreground',
      )}
    >
      {bpToPercent(bp)}%{changed ? (bp > prev ? ' ↑' : ' ↓') : null}
    </span>
  )
}

function MonthCell({
  children,
  note,
  tip,
}: {
  children: React.ReactNode
  note?: React.ReactNode
  tip?: React.ReactNode
}) {
  const body = (
    <div className="flex flex-col items-end leading-tight">
      <span className="tabular-nums">{children}</span>
      {note}
    </div>
  )
  return (
    <TableCell className="min-w-24 text-right align-top">
      {tip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* biome-ignore lint/a11y/noNoninteractiveTabindex: focus opens the tooltip for keyboard users */}
            <div tabIndex={0} className="cursor-default outline-none">
              {body}
            </div>
          </TooltipTrigger>
          <TooltipContent>{tip}</TooltipContent>
        </Tooltip>
      ) : (
        body
      )}
    </TableCell>
  )
}

function TipLines({ lines }: { lines: [string, string][] }) {
  return (
    <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
      {lines.map(([k, v]) => (
        <Fragment key={k}>
          <dt>{k}</dt>
          <dd className="text-right tabular-nums">{v}</dd>
        </Fragment>
      ))}
    </dl>
  )
}

function ProfitShareMonthlyTable({
  months,
  employees,
  showYears,
}: {
  months: MonthProfitShare[]
  employees: ProfitShareEmployeeRow[]
  showYears: boolean
}) {
  if (months.length === 0) {
    return <p className="text-sm text-muted-foreground">No months yet.</p>
  }
  const sum = (f: (m: MonthProfitShare) => number) =>
    months.reduce((s, m) => s + f(m), 0)
  const people = employees.filter((e) =>
    months.some(
      (m) =>
        m.snapshot?.employees.some((x) => x.id === e.id) ||
        (m.income.commissionsByEmployee[e.id] ?? 0) > 0,
    ),
  )
  const years: { year: string; count: number }[] = []
  for (const m of months) {
    const year = m.month.slice(0, 4)
    const last = years.at(-1)
    if (last?.year === year) last.count++
    else years.push({ year, count: 1 })
  }
  const paid = (m: MonthProfitShare, id: string) =>
    m.share.employees.find((e) => e.id === id)?.totalMicros ??
    m.income.commissionsByEmployee[id] ??
    0

  return (
    <Table className="w-auto min-w-full">
      <TableHeader>
        {showYears ? (
          <TableRow>
            <TableHead className={STICKY_LEFT} />
            {years.map((y) => (
              <TableHead key={y.year} colSpan={y.count} className="border-l">
                {y.year}
              </TableHead>
            ))}
            <TableHead className={STICKY_RIGHT} />
          </TableRow>
        ) : null}
        <TableRow>
          <TableHead className={STICKY_LEFT} />
          {months.map((m) => (
            <TableHead key={m.month} className="text-right">
              {monthKeyShortLabel(m.month)}
            </TableHead>
          ))}
          <TableHead className={STICKY_RIGHT}>Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell className={STICKY_LEFT}>Net income</TableCell>
          {months.map((m) => (
            <MonthCell
              key={m.month}
              tip={
                m.share.commissionMicros > 0 ? (
                  <TipLines
                    lines={[
                      ['Received', formatMicroUsd(m.income.totalMicros)],
                      ['Commissions', formatMicroUsd(m.share.commissionMicros)],
                    ]}
                  />
                ) : undefined
              }
            >
              {formatMicroUsd(m.share.netRevenueMicros)}
            </MonthCell>
          ))}
          <TableCell className={STICKY_RIGHT}>
            {formatMicroUsd(sum((m) => m.share.netRevenueMicros))}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={STICKY_LEFT}>Pool</TableCell>
          {months.map((m, i) => (
            <MonthCell
              key={m.month}
              note={
                m.snapshot ? (
                  <PercentNote
                    bp={m.snapshot.poolShareBp}
                    prev={months[i - 1]?.snapshot?.poolShareBp ?? null}
                  />
                ) : undefined
              }
            >
              {formatMicroUsd(m.share.poolMicros)}
            </MonthCell>
          ))}
          <TableCell className={STICKY_RIGHT}>
            {formatMicroUsd(sum((m) => m.share.poolMicros))}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={STICKY_LEFT}>Base</TableCell>
          {months.map((m, i) => {
            const headcount = m.share.employees.length
            return (
              <MonthCell
                key={m.month}
                note={
                  m.snapshot ? (
                    <PercentNote
                      bp={m.snapshot.baseShareBp}
                      prev={months[i - 1]?.snapshot?.baseShareBp ?? null}
                    />
                  ) : undefined
                }
                tip={
                  headcount > 0
                    ? `${formatMicroUsd(m.share.employees[0]?.baseMicros ?? 0)} each across ${headcount}`
                    : 'Nobody in the pool'
                }
              >
                {formatMicroUsd(m.share.basePoolMicros)}
              </MonthCell>
            )
          })}
          <TableCell className={STICKY_RIGHT}>
            {formatMicroUsd(sum((m) => m.share.basePoolMicros))}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={STICKY_LEFT}>Discretionary</TableCell>
          {months.map((m) => (
            <MonthCell key={m.month}>
              {formatMicroUsd(m.share.discretionaryPoolMicros)}
            </MonthCell>
          ))}
          <TableCell className={STICKY_RIGHT}>
            {formatMicroUsd(sum((m) => m.share.discretionaryPoolMicros))}
          </TableCell>
        </TableRow>
        {people.map((e) => (
          <TableRow key={e.id}>
            <TableCell className={cn(STICKY_LEFT, 'font-medium')}>
              <span className="flex items-center gap-2">
                {e.name}
                {e.archivedAt ? (
                  <Badge variant="secondary">Archived</Badge>
                ) : null}
              </span>
            </TableCell>
            {months.map((m, i) => {
              const s = m.share.employees.find((x) => x.id === e.id)
              const commission = m.income.commissionsByEmployee[e.id] ?? 0
              if (!s) {
                return commission > 0 ? (
                  <MonthCell
                    key={m.month}
                    note={
                      <span className="text-xs text-muted-foreground">
                        commission
                      </span>
                    }
                    tip="Not in the pool this month"
                  >
                    {formatMicroUsd(commission)}
                  </MonthCell>
                ) : (
                  <MonthCell key={m.month}>
                    <span className="text-muted-foreground">—</span>
                  </MonthCell>
                )
              }
              const prev =
                months[i - 1]?.snapshot?.employees.find((x) => x.id === e.id)
                  ?.discretionaryBp ?? null
              return (
                <MonthCell
                  key={m.month}
                  note={<PercentNote bp={s.discretionaryBp} prev={prev} />}
                  tip={
                    <TipLines
                      lines={[
                        ...(s.commissionMicros > 0
                          ? [
                              [
                                'Commission',
                                formatMicroUsd(s.commissionMicros),
                              ] as [string, string],
                            ]
                          : []),
                        ['Base', formatMicroUsd(s.baseMicros)],
                        [
                          `Discretionary (${bpToPercent(s.discretionaryBp)}%)`,
                          formatMicroUsd(s.discretionaryMicros),
                        ],
                      ]}
                    />
                  }
                >
                  {formatMicroUsd(s.totalMicros)}
                </MonthCell>
              )
            })}
            <TableCell className={cn(STICKY_RIGHT, 'font-medium')}>
              {formatMicroUsd(sum((m) => paid(m, e.id)))}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell className={STICKY_LEFT}>
            <span className="flex items-center gap-1">
              Unassigned
              <InfoTip label="About unassigned">{UNASSIGNED_TIP}</InfoTip>
            </span>
          </TableCell>
          {months.map((m) => (
            <MonthCell key={m.month}>
              {formatMicroUsd(m.share.unallocatedMicros)}
            </MonthCell>
          ))}
          <TableCell className={STICKY_RIGHT}>
            {formatMicroUsd(sum((m) => m.share.unallocatedMicros))}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}

function PercentField({
  id,
  label,
  value,
  onChange,
  hint,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.01}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="max-w-32"
        />
        <span className="text-sm text-muted-foreground">%</span>
      </div>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

function parsePercent(v: string): number | null {
  const n = Number(v)
  if (!Number.isFinite(n) || n < 0 || n > 100) return null
  return percentToBp(n)
}

function ScopeField({
  viewed,
  current,
  value,
  onChange,
}: {
  viewed: MonthKey
  current: MonthKey
  value: EditScope
  onChange: (v: EditScope) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="ps-scope">Apply to</Label>
      <Select value={value} onValueChange={(v) => onChange(v as EditScope)}>
        <SelectTrigger id="ps-scope" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="current">
            This month ({monthKeyLabel(current)})
          </SelectItem>
          <SelectItem value="from">
            {monthKeyLabel(viewed)} through {monthKeyLabel(current)}
          </SelectItem>
          <SelectItem value="only">{monthKeyLabel(viewed)} only</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

function ConflictList({ changes }: { changes: ProfitShareEditChange[] }) {
  if (changes.length === 0) return null
  const pct = (bp: number | null) =>
    bp === null ? 'not in pool' : `${bpToPercent(bp)}%`
  return (
    <Alert>
      <Info />
      <AlertTitle>These months had a different value</AlertTitle>
      <AlertDescription>
        <ul>
          {changes.map((c) => (
            <li key={`${c.month}-${c.field}`} className="tabular-nums">
              {monthKeyLabel(c.month)}:{' '}
              {c.field === 'discretionary' ? '' : `${c.field} `}
              {pct(c.from)} → {pct(c.to)}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  )
}

function savedMessage(
  viewed: MonthKey,
  current: MonthKey,
  scope: EditScope,
  fallback: string,
): string {
  return viewed < current && scope === 'current'
    ? `Saved for ${monthKeyLabel(current)}`
    : fallback
}

function SettingsDialog({
  config,
  snapshot,
  viewed,
  onClose,
}: {
  config: ProfitShareConfig
  snapshot: ProfitShareSnapshot
  viewed: MonthKey
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [pool, setPool] = useState(String(bpToPercent(snapshot.poolShareBp)))
  const [base, setBase] = useState(String(bpToPercent(snapshot.baseShareBp)))
  const [scope, setScope] = useState<EditScope>('current')
  const poolBp = parsePercent(pool)
  const baseBp = parsePercent(base)
  const past = viewed < config.currentMonth
  const changed =
    poolBp !== snapshot.poolShareBp || baseBp !== snapshot.baseShareBp
  const conflicts =
    past && changed && poolBp !== null && baseBp !== null
      ? editConflicts({
          snapshots: config.snapshots,
          viewed,
          current: config.currentMonth,
          scope,
          edit: { kind: 'settings', poolShareBp: poolBp, baseShareBp: baseBp },
        })
      : []

  const save = useMutation({
    mutationFn: () =>
      updateProfitShareSettings({
        data: {
          month: viewed,
          scope: past ? scope : 'current',
          poolShareBp: poolBp ?? 0,
          baseShareBp: baseBp ?? 0,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
      toast.success(
        savedMessage(viewed, config.currentMonth, scope, 'Settings saved'),
      )
      onClose()
    },
    onError: (e) =>
      toast.error("Couldn't save", { description: (e as Error).message }),
  })

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Settings for {monthKeyLabel(viewed)}</DialogTitle>
        <DialogDescription>
          The pool is a cut of the month's income. Part of it is split equally
          across employees in the pool, the rest is assigned per employee.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4">
        <PercentField
          id="ps-pool"
          label="Pool share of income"
          value={pool}
          onChange={setPool}
        />
        <PercentField
          id="ps-base"
          label="Base share of pool"
          value={base}
          onChange={setBase}
          hint={
            baseBp !== null
              ? `${bpToPercent(10_000 - baseBp)}% of the pool is discretionary.`
              : undefined
          }
        />
        {past && changed ? (
          <ScopeField
            viewed={viewed}
            current={config.currentMonth}
            value={scope}
            onChange={setScope}
          />
        ) : null}
        <ConflictList changes={conflicts} />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <PendingButton
          type="button"
          pending={save.isPending}
          disabled={!changed || poolBp === null || baseBp === null}
          onClick={() => save.mutate()}
        >
          Save
        </PendingButton>
      </DialogFooter>
    </DialogContent>
  )
}

function EmployeeDialog({
  row,
  config,
  viewed,
  onClose,
}: {
  row: EmployeeShareRow | null
  config: ProfitShareConfig
  viewed: MonthKey
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(row?.name ?? '')
  const [inPool, setInPool] = useState(row?.inPool ?? true)
  const [share, setShare] = useState(
    String(bpToPercent(row?.discretionaryBp ?? 0)),
  )
  const [scope, setScope] = useState<EditScope>('current')
  const shareBp = parsePercent(share)
  const past = viewed < config.currentMonth
  const poolChanged = row
    ? inPool !== row.inPool || (inPool && shareBp !== row.discretionaryBp)
    : inPool
  const nameChanged = name.trim() !== (row?.name ?? '')
  const discretionaryBp = inPool ? shareBp : null
  const conflicts =
    row && past && poolChanged && (!inPool || shareBp !== null)
      ? editConflicts({
          snapshots: config.snapshots,
          viewed,
          current: config.currentMonth,
          scope,
          edit: { kind: 'employee', employeeId: row.id, discretionaryBp },
        })
      : []
  const done = (msg: string) => {
    void queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
    toast.success(msg)
    onClose()
  }

  const save = useMutation({
    mutationFn: () =>
      saveProfitShareEmployee({
        data: {
          ...(row ? { id: row.id } : {}),
          name: name.trim(),
          ...(poolChanged
            ? {
                pool: {
                  month: viewed,
                  scope: past ? scope : 'current',
                  discretionaryBp,
                },
              }
            : {}),
        },
      }),
    onSuccess: () =>
      done(
        poolChanged
          ? savedMessage(
              viewed,
              config.currentMonth,
              scope,
              row ? 'Employee updated' : 'Employee added',
            )
          : row
            ? 'Employee updated'
            : 'Employee added',
      ),
    onError: (e) =>
      toast.error("Couldn't save", { description: (e as Error).message }),
  })
  const archive = useMutation({
    mutationFn: () =>
      archiveProfitShareEmployee({ data: { id: row?.id ?? '' } }),
    onSuccess: () => done('Employee archived'),
    onError: (e) =>
      toast.error("Couldn't archive", { description: (e as Error).message }),
  })

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{row ? 'Edit employee' : 'Add employee'}</DialogTitle>
        <DialogDescription>
          Everyone in the pool gets an equal slice of the base. The
          discretionary share is theirs alone, and all shares together may not
          exceed 100%.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="ps-name">Name</Label>
          <Input
            id="ps-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            maxLength={100}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="ps-in-pool">
            In the pool for {monthKeyLabel(viewed)}
          </Label>
          <Switch
            id="ps-in-pool"
            checked={inPool}
            onCheckedChange={setInPool}
          />
        </div>
        {inPool ? (
          <PercentField
            id="ps-share"
            label="Discretionary share"
            value={share}
            onChange={setShare}
          />
        ) : null}
        {past && poolChanged ? (
          <ScopeField
            viewed={viewed}
            current={config.currentMonth}
            value={scope}
            onChange={setScope}
          />
        ) : null}
        <ConflictList changes={conflicts} />
      </div>
      <DialogFooter className="sm:justify-between">
        {row ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <PendingButton
                type="button"
                variant="destructive"
                pending={archive.isPending}
                onClick={() => archive.mutate()}
              >
                Archive
              </PendingButton>
            </TooltipTrigger>
            <TooltipContent>
              Leaves the pool from {monthKeyLabel(config.currentMonth)} on.
              Earlier months keep their shares.
            </TooltipContent>
          </Tooltip>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <PendingButton
            type="button"
            pending={save.isPending}
            disabled={
              name.trim().length === 0 ||
              (inPool && shareBp === null) ||
              (row !== null && !poolChanged && !nameChanged)
            }
            onClick={() => save.mutate()}
          >
            {row ? 'Save' : 'Add'}
          </PendingButton>
        </div>
      </DialogFooter>
    </DialogContent>
  )
}

const RECORDED_AT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

function PaymentsDialog({
  org,
  period,
  canEdit,
}: {
  org: IncomeOrgRow
  period: IncomePeriod
  canEdit: boolean
}) {
  const queryClient = useQueryClient()
  const payments = useQuery({
    queryKey: ['admin-income', 'payments', org.orgId, period],
    queryFn: () =>
      getOrgIncomePayments({ data: { organizationId: org.orgId, period } }),
  })
  const move = useMutation({
    mutationFn: (data: { ledgerEntryId: string; month: MonthKey | null }) =>
      movePaymentMonth({ data }),
    onSuccess: (_, { month }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-income'] })
      toast.success(
        month
          ? `Payment counts in ${monthKeyLabel(month)}`
          : 'Payment back in its recorded month',
      )
    },
    onError: (e) =>
      toast.error("Couldn't move", { description: (e as Error).message }),
  })

  return (
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>Payments from {org.orgName}</DialogTitle>
        <DialogDescription>
          Recorded or counted in {periodLabel(period)}. Times are UTC, the same
          clock the months use. A payment can count in the month before or after
          the one it was recorded in.
        </DialogDescription>
      </DialogHeader>
      {payments.data === undefined ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recorded</TableHead>
              <TableHead>Paid via</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Counts in</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.data.map((p) => {
              const recorded = monthKeyOfDate(new Date(p.recordedAt))
              const counted = p.countedMonth ?? recorded
              return (
                <TableRow key={p.ledgerEntryId}>
                  <TableCell className="tabular-nums">
                    {RECORDED_AT.format(new Date(p.recordedAt))}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {p.rail ? (
                        <Badge variant="outline">{p.rail}</Badge>
                      ) : null}
                      {p.kind === 'refund' ? (
                        <Badge variant="secondary">Refund</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMicroUsd(p.micros)}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Select
                        value={counted}
                        disabled={move.isPending}
                        onValueChange={(month) =>
                          move.mutate({
                            ledgerEntryId: p.ledgerEntryId,
                            month: month === recorded ? null : month,
                          })
                        }
                      >
                        <SelectTrigger
                          size="sm"
                          className="w-44"
                          aria-label="Counts in"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[-1, 0, 1].map((n) => {
                            const m = addMonths(recorded, n)
                            return (
                              <SelectItem key={m} value={m}>
                                {monthKeyLabel(m)}
                              </SelectItem>
                            )
                          })}
                        </SelectContent>
                      </Select>
                    ) : (
                      monthKeyLabel(counted)
                    )}
                    {p.countedMonth ? (
                      <Badge variant="secondary" className="ml-2">
                        Moved
                      </Badge>
                    ) : null}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </DialogContent>
  )
}

function CommissionDialog({
  org,
  period,
  employees,
  onClose,
}: {
  org: IncomeOrgRow
  period: IncomePeriod
  employees: ProfitShareEmployeeRow[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [employeeId, setEmployeeId] = useState(employees[0]?.id ?? '')
  const [rate, setRate] = useState('10')
  const rateBp = parsePercent(rate)
  const preview =
    rateBp !== null ? Math.floor((org.micros * rateBp) / 10_000) : null

  const save = useMutation({
    mutationFn: () =>
      addPaymentCommission({
        data: {
          organizationId: org.orgId,
          period,
          employeeId,
          rateBp: rateBp ?? 0,
        },
      }),
    onSuccess: ({ payments }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-income'] })
      toast.success(
        `Commission added on ${payments} payment${payments === 1 ? '' : 's'}`,
      )
      onClose()
    },
    onError: (e) =>
      toast.error("Couldn't add commission", {
        description: (e as Error).message,
      }),
  })

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Commission on {org.orgName}</DialogTitle>
        <DialogDescription>
          Applies to every payment this organization made in{' '}
          {periodLabel(period)}. The amount comes off income before the
          profit-share pool and is paid to the employee.
        </DialogDescription>
      </DialogHeader>
      {employees.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Add an employee under Profit sharing first.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="pc-employee">Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger id="pc-employee" className="w-full">
                <SelectValue placeholder="Pick an employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PercentField
            id="pc-rate"
            label="Commission rate"
            value={rate}
            onChange={setRate}
            hint={
              preview !== null
                ? `${formatMicroUsd(preview)} on ${formatMicroUsd(org.micros)} received.`
                : undefined
            }
          />
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <PendingButton
          type="button"
          pending={save.isPending}
          disabled={!employeeId || rateBp === null || rateBp === 0}
          onClick={() => save.mutate()}
        >
          Add commission
        </PendingButton>
      </DialogFooter>
    </DialogContent>
  )
}
